import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	AuthnService,
	BreadcrumbsService,
	ComponentBase,
	DeploymentContext,
	GroupedMenuOptions,
	MenuOption,
	MenuOptionService,
	SearchService,
	WebAnalyticsService,
} from '@Common';
import {
	Filter,
	Location,
	StatusDisplayItem,
	determineSectorsAndSubsectors,
	LocalizedTextIds,
	Mode,
	SectorWithChildOptions,
} from 'company-finder-common';
import { SearchResultsComponent } from '../search-results';

@Component({
	selector: 'filter',
	templateUrl: './filter.component.html',
	styleUrls: ['./filter.component.scss'],
	providers: [MenuOptionService],
	standalone: false,
})
export class FilterComponent
	extends ComponentBase
	implements OnDestroy, OnInit
{
	@Input()
	public isSearchResultsScreen = false;

	@Input()
	public singleSector = false;

	/** An explicit list of the "hidden" filter constraints (ADJQ-199).
	 * Note that this is dynamically augmented with 0 or more potential tag values.
	 */
	private additionalFilterConstraints: AdditionalFilterConstraint[] = [];

	public constructor(
		dc: DeploymentContext,
		private searchService: SearchService,
		private webAnalyticsService: WebAnalyticsService,
		private menuOptionService: MenuOptionService,
		private authnService: AuthnService,
		private breadcrumbsService: BreadcrumbsService,
		private route: ActivatedRoute
	) {
		super(dc);

		if (this.featureSwitches.enableSectors) {
			this.additionalFilterConstraints.push(
				new AdditionalFilterConstraint(
					() => this.Loc(LocalizedTextIds.FilterCrossSector),
					() => this.searchService.filter.isCrossSector,
					() => (this.searchService.filter.isCrossSector = false)
				)
			);
		}
		this.additionalFilterConstraints.push(
			new AdditionalFilterConstraint(
				() => this.Loc(LocalizedTextIds.FilterLastDays),
				() => this.searchService.filter.isNewInLast90Days,
				() => (this.searchService.filter.isNewInLast90Days = false)
			)
		);
		this.additionalFilterConstraints.push(
			new AdditionalFilterConstraint(
				() => this.Loc(LocalizedTextIds.FilterLastQuarter),
				() => this.searchService.filter.isNewInLastQuarter,
				() => (this.searchService.filter.isNewInLastQuarter = false)
			)
		);
		if (this.featureSwitches.enableBlueKnight) {
			this.additionalFilterConstraints.push(
				new AdditionalFilterConstraint(
					() => this.Loc(LocalizedTextIds.BlueKnight),
					() => this.searchService.filter.isBlueKnight,
					() => (this.searchService.filter.isBlueKnight = false)
				)
			);
		}
		if (this.featureSwitches.enableQfc) {
			this.additionalFilterConstraints.push(
				new AdditionalFilterConstraint(
					() => this.Loc(LocalizedTextIds.QFCAwardee),
					() => this.searchService.filter.isQFCWinner,
					() => (this.searchService.filter.isQFCWinner = false)
				)
			);
		}
		this.additionalFilterConstraints.push(
			new AdditionalFilterConstraint(
				() => this.searchService.filter.currentRdStage,
				() => !!this.searchService.filter.currentRdStage,
				() => (this.searchService.filter.currentRdStage = undefined)
			)
		);
	}

	public title = this.Loc(LocalizedTextIds.FilterFilters);
	public sectorLabel = this.Loc(LocalizedTextIds.Sector);

	public get followedCompaniesOnly(): boolean {
		return this.searchService.filter?.isFollowedCompaniesOnly;
	}

	public set followedCompaniesOnly(value: boolean) {
		this.searchService.filter.isFollowedCompaniesOnly = value;
		this.applyFilter();
		this.trackForAnalytics('isFollowedCompaniesOnly', value);
	}

	public get followedAssetsOnly(): boolean {
		return this.searchService.filter?.isFollowedAssetsOnly;
	}

	public set followedAssetsOnly(value: boolean) {
		this.searchService.filter.isFollowedAssetsOnly = value;
		this.applyFilter();
		this.trackForAnalytics('isfollowedAssetsOnly', value);
	}

	public get enableNavigateToSearch(): boolean {
		return this._deploymentContext.rawConfig.behavior
			.drillDownNavigatesToSearchResults;
	}

	public get hasSearchPredicate(): boolean {
		return !!this.searchService.currentSearchPredicate;
	}

	public get showClearFilterAction(): boolean {
		// For the Asset Explore page, a user is required to have a sector,
		// so in that case we treat a filter with a single sector as "empty"
		// in that there is nothing to be cleared
		const filterToCheck = !this.singleSector
			? this.searchService.filter
			: new Filter({
					...this.searchService.filter,
					primarySectors: [],
				});
		// The keyword search predicate lives in the search bar rather than in
		// the filter itself, but Clear All clears it too (see clearFilter), so
		// an active predicate alone is enough to warrant offering the action.
		return !filterToCheck.isShowAll() || this.hasSearchPredicate;
	}

	public get isFollowEnabled(): boolean {
		return this._deploymentContext.featureSwitches.enableFollowCompanies;
	}

	public get isInternalView(): boolean {
		return this.authnService.isInternal;
	}

	public get locationOptions(): MenuOption<Location>[] {
		return this.menuOptionService.locationOptions;
	}

	public get indicationOptions(): MenuOption<string>[] {
		return this.menuOptionService.indicationOptions;
	}
	public get therapeuticAreaOptions(): MenuOption<string>[] {
		return this.menuOptionService.therapeuticAreaOptions;
	}

	public get statusOptions(): GroupedMenuOptions<StatusDisplayItem>[] {
		return this.menuOptionService.statusOptions;
	}

	public get hasAdditionalFilterConstraints(): boolean {
		return this.activeAdditionalFilterConstraints.length > 0;
	}

	public get optionsForSingleRow(): MenuOption<StatusDisplayItem>[] {
		return this.statusOptions[0]?.options ?? [];
	}

	/** This will augment the base set of AdditionalFilterConstraints with any selected tag values */
	public get activeAdditionalFilterConstraints(): AdditionalFilterConstraint[] {
		if (!this.searchService.filter) {
			return [];
		}

		const tagFilterConstraints = new Array<AdditionalFilterConstraint>();
		this.searchService.filter.tags?.forEach((tagValue) => {
			tagFilterConstraints.push(
				new AdditionalFilterConstraint(
					() => tagValue,
					() => true,
					() =>
						(this.searchService.filter.tags =
							this.searchService.filter.tags.filter(
								(value) => value !== tagValue
							))
				)
			);
		});

		return [
			...this.additionalFilterConstraints,
			...tagFilterConstraints,
		].filter((item) => item.hasValue());
	}

	public get showLocationSelector(): boolean {
		return (
			this.mode !== this.Mode.Asset &&
			this.featureSwitches.enableShowLocations &&
			this.locationOptions?.length > 2
		);
	}

	public get hidePrimarySectorSelector(): boolean {
		return !this.featureSwitches.enableSectors;
	}

	public get hideIndicationSelector(): boolean {
		return (
			this.mode !== this.Mode.Company ||
			!this.featureSwitches.enableIndicationFilter
		);
	}

	public get hideSecondarySelector(): boolean {
		return (
			this.mode === this.Mode.Asset ||
			!this.featureSwitches.enableSectors ||
			!this.featureSwitches.enableSecondarySectors
		);
	}

	public get isCompanyMode(): boolean {
		return this.mode === this.Mode.Company;
	}

	async ngOnInit(): Promise<void> {
		this.searchService.filter = this.searchService.filter ?? new Filter();

		if (this.singleSector) {
			this.currentSector =
				this.searchService.filter.primarySectors.length > 0
					? this.searchService.filter.primarySectors[0]
					: this.sectors[0];
		} else {
			this.currentSector = null;
		}

		this.menuOptionService.initMenus(this.searchService.filter);

		this.addSubscription(
			this.searchService.drilldownSubject.subscribe((mode: Mode) => {
				this.mode = mode;

				const currentlyOnSearchResultsView =
					this.route.component.name === SearchResultsComponent.name;

				// Whenever we drilldown, reset the saved position in the search results page
				this.breadcrumbsService.setSearchPageOffset(0);

				// FUTURE: The quick implementation of ADJQ-210 (and maybe partly due to ADJQ-184) resulted in this logic getting complicated!
				//  But it avoids an infinite recursion when drillDownNavigatesToSearchResults is off and filtering when on the search-results page.
				//  Seems to be due to an interaction between filterSubject and drilldownSubject subscribers,
				//  but I didn't have the time to unravel that when implementing ADJQ-210, hence the logic tree below.
				if (
					(this.authnService.isAuthenticatedUser ||
						!this._deploymentContext.featureSwitches
							.enablePaywall) &&
					(this.enableNavigateToSearch ||
						currentlyOnSearchResultsView ||
						this.hasSearchPredicate)
				) {
					this.searchService.navigateToSearchResults();
				} else {
					// This seems sufficient/necessary to refresh the Explore view,
					// but it would infinitely recurse if called when already on the search results view
					// FUTURE - it may be worth checking if the above statement is still true.
					this.applyFilter();
				}
			})
		);

		// listen for message indicating click from parent frame
		if (this._deploymentContext?.hosted()) {
			this._deploymentContext.listenForClick();
		}
	}

	public ngOnDestroy(): void {
		if (this._subscriptions) {
			this._subscriptions.unsubscribe();
			this._subscriptions = undefined;
		}
	}

	public updateLocationFilter(selectedLocations: Location[]): void {
		this.searchService.filter.locations = selectedLocations.map(
			(loc) => loc.name
		);
		this.applyFilter();
	}

	public updateIndicationFilter(selectedIndications: string[]): void {
		this.searchService.filter.indications = selectedIndications;
		this.applyFilter();
	}

	public updateFocusAreaFilter(
		selectedFocusAreas: SectorWithChildOptions[]
	): void {
		this.searchService.filter.focusAreas = selectedFocusAreas.map(
			(opt) => opt.sectorOrOption
		);
		this.applyFilter();
	}

	public updateTherapeuticAreaFilter(
		selectedTherapeuticAreas: string[]
	): void {
		this.searchService.filter.therapeuticAreas = selectedTherapeuticAreas;
		this.applyFilter();
	}

	public updateDevelopmentStageFilter(
		selectedDevelopmentStages: SectorWithChildOptions[]
	): void {
		this.searchService.filter.developmentStages =
			selectedDevelopmentStages.map((opt) => opt.sectorOrOption);
		this.applyFilter();
	}

	public updateSectorFilter(selectedSectors: SectorWithChildOptions[]): void {
		const [sectors, subsectors] =
			determineSectorsAndSubsectors(selectedSectors);
		this.searchService.filter.primarySectors = sectors;
		this.searchService.filter.primarySubSectors = subsectors;
		this.applyFilter();
	}

	public updateSecondarySectorFilter(
		selectedSectors: SectorWithChildOptions[]
	): void {
		const [sectors, subsectors] =
			determineSectorsAndSubsectors(selectedSectors);
		this.searchService.filter.secondarySectors = sectors;
		this.searchService.filter.secondarySubSectors = subsectors;
		this.applyFilter();
	}

	public updateStatusFilter(): void {
		const allOptions = [];

		this.statusOptions.forEach((group) => {
			allOptions.push(...group.options);
		});

		const selectedStatuses = allOptions
			.filter((statusOption) => statusOption.value)
			.map((selectedOption) => selectedOption.dataModel.status as string);

		const statuses =
			this._deploymentContext.groupStatuses(selectedStatuses);

		this.searchService.filter.locationStatuses =
			statuses.locationStatuses.toString();
		this.searchService.filter.companyStatuses =
			statuses.companyStatuses.toString();
		this.applyFilter();
	}

	public get useStatusDropdown(): boolean {
		return this._deploymentContext.statusMetadata?.length > 1;
	}

	public currentSector;
	public changeSector(sector) {
		// When changing sectors, clear any dependent filters
		this.searchService.filter.primarySectors = [sector];
		this.searchService.filter.primarySubSectors = [];
		this.searchService.filter.focusAreas = [];
		this.searchService.filter.developmentStages = [];
		this.applyFilter();
	}

	public get sectors() {
		return this._deploymentContext.sectorNames;
	}

	public clearAdditionalFilterConstraint(
		item: AdditionalFilterConstraint
	): void {
		item.clearValue();
		this.applyFilter();
	}

	public clearFilter(): void {
		// If we require exactly one sector, keep the currently
		// selected sector in an otherwise-empty filter
		this.searchService.filter.clear(this.singleSector);

		// The keyword search predicate is owned by the search bar rather than
		// by the filter, but Clear All is expected to clear every active search
		// criteria. Clearing it here (ahead of the re-search below) keeps both
		// the results and the 'text' query param in sync with the emptied filter.
		this.searchService.clearSearchPredicate();

		this.menuOptionService.initMenus(this.searchService.filter);
		this.applyFilter();
		this.webAnalyticsService.trackEvent('clear-filter');
		if (this.isSearchResultsScreen) {
			this.searchService.navigateToSearchResults();
		}
	}

	public applyFilter() {
		this.searchService.filterSubject.next(this.mode);
	}

	public trackForAnalytics(
		trackingCriteria: string,
		value: boolean | MenuOption<unknown>
	): void {
		this.webAnalyticsService.trackEvent('filter', {
			category: `Filter by ${trackingCriteria}`,
			label:
				typeof value === 'boolean' || !value
					? `${value}`
					: `${(value as MenuOption<unknown>).label} - ${
							(value as MenuOption<unknown>).value
						}`,
			// FUTURE: This feels a bit fragile, but if we don't have a count yet from a previous search, assume we are working off the full set
			value:
				this.searchService.companyCountFromLastSearch ||
				this._deploymentContext.comprehensiveSummary?.numberOfCompanies,
		});
	}
}

class AdditionalFilterConstraint {
	constructor(
		public displayString: () => string,
		public hasValue: () => boolean,
		public clearValue: () => void
	) {}
}
